/**
 * Three.js Plugin
 *
 * Provides 3D graphics functionality using Three.js.
 * Registers the 'three' endpoint type and all Three.js nodes.
 */

// Register three sub-datatypes for type-safe connections
declare module '@nodes/Endpoint' {
  interface DataTypeRegistry {
    'three:geometry': 'three:geometry';
    'three:group': 'three:group';
    'three:light': 'three:light';
    'three:scene': 'three:scene';
    'three:shape': 'three:shape';
  }
}

import type { Plugin, EndpointWithContext } from '@plugins/Plugin';
import type { Dependencies } from '@nodes/Dependencies';
import { Domain } from '@nodes/Endpoint';

import type { Three } from './types';

export interface ThreeDependencies extends Dependencies {
  three: Three;
}
import { isBrowser } from '@runtime/env';
import { registerSerializer } from '@/runtime/serializerRegistry';
import { createThreeAdapter } from './instancedMeshBuilder';
import type { RepeaterDomainAdapter } from '@/nodes/Dependencies';
import { ThreeEndpoint } from './ThreeEndpoint';
import { geometryNodes } from './nodes/GeometryNode';
import { materialNodes } from './nodes/materials';
import { lightNodes } from './nodes/lights';
import { sceneNodeDef } from './nodes/SceneNode';
import { transformNodeDef } from './nodes/TransformNode';
import { mergeGeometryNodeDef } from './nodes/MergeGeometryNode';
import { terrainNodeDef } from './nodes/TerrainNode';
import { splineNodeDef } from './nodes/SplineNode';
import { extrudeNodeDef } from './nodes/ExtrudeNode';
import { latheNodeDef } from './nodes/LatheNode';
import { tubeNodeDef } from './nodes/TubeNode';
import { booleanNodeDef } from './nodes/BooleanNode';
import { outlineNodeDef } from './nodes/OutlineNode';
import { groupMeshNodeDef } from './nodes/GroupMeshNode';
import { cameraNodes } from './nodes/CameraNode';

import { polylineGeometryNodeDef } from './nodes/PolylineGeometryNode';
import { mocapNodeDef } from './mocap/MocapNode';

// three module - loaded on register
let three: Three | null = null;

/** Return the cached Three.js module (available after plugin register) */
export function getThree(): Three | null {
  return three;
}

const allNodes = [
  ...geometryNodes,
  ...materialNodes,
  ...lightNodes,
  sceneNodeDef,
  transformNodeDef,
  mergeGeometryNodeDef,
  terrainNodeDef,
  splineNodeDef,
  outlineNodeDef,
  extrudeNodeDef,
  latheNodeDef,
  tubeNodeDef,
  booleanNodeDef,
  groupMeshNodeDef,
  polylineGeometryNodeDef,
  mocapNodeDef,
  ...cameraNodes
];

export const threejsPlugin: Plugin = {
  id: 'threejs',
  name: 'Three.js',
  namespace: 'three',
  domain: Domain.Video,

  // All Three.js nodes
  nodes: allNodes,

  // Register three sub-datatypes
  endpointDataTypes: ['three:geometry', 'three:group', 'three:light', 'three:scene', 'three:shape'],

  async onRegister() {
    // Only load in browser
    if (!isBrowser) return;

    try {
      three = await import('three');
    } catch (err) {
      console.error('Failed to load three:', err);
    }

    // Register serializers for three.js types
    registerSerializer('three:shape', (shape: any) => {
      if (!shape) return null;
      const outer = shape.getPoints?.(64)?.map((p: any) => [p.x, p.y]) ?? [];
      const holes = shape.holes?.map((hole: any) =>
        hole.getPoints?.(64)?.map((p: any) => [p.x, p.y]) ?? []
      ) ?? [];
      return { outer, holes };
    });
  },

  getRepeaterAdapters(): RepeaterDomainAdapter[] {
    return [createThreeAdapter(() => three!)];
  },

  createNode(type: string, id: string, deps: any) {
    const nodeDef = this.nodes!.find(n => n.type === type);
    if (!nodeDef) {
      throw new Error(`Unknown Three.js node type: ${type}`);
    }
    return new nodeDef.ctor(id, { ...deps, three });
  },

  createEndpoint(config: EndpointWithContext) {
    return new ThreeEndpoint(config);
  },

  async registerUI({ registerViz, registerVisualiser }) {
    const { OrbitCanvasVisualization } = await import('./ui/OrbitCanvasVisualization');
    const { SplineVisualization } = await import('./ui/SplineVisualization');
    const { TerrainVisualization } = await import('./ui/TerrainVisualization');
    const { CanvasVisualization } = await import('@/plugins/video/ui/CanvasVisualization');
    const { meshVisualiser } = await import('@/ui/performance/visualisers/MeshVisualiser');
    registerViz('PerspectiveCamera', OrbitCanvasVisualization);
    registerViz('OrthographicCamera', OrbitCanvasVisualization);
    registerViz('Spline2D', SplineVisualization);
    registerViz('Terrain', TerrainVisualization);
    registerViz('Outline', CanvasVisualization);
    registerVisualiser('mesh', meshVisualiser);
  }
};
